import { ChevronRight, type LucideIcon } from "lucide-react";

import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import { NavLink, Link } from "react-router";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type NavSecondaryItem = {
	title: string;
	icon: LucideIcon;
	badge?: React.ReactNode;
} & (
	| { url: string; external?: boolean; items?: never }
	| { url?: never; external?: never; items: { title: string; url: string }[] }
);

export type NavSecondaryItems = NavSecondaryItem[];

export function NavSecondary({
	items,
	...props
}: {
	items: NavSecondaryItems;
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
	return (
		<SidebarGroup {...props}>
			<SidebarGroupContent>
				<SidebarMenu>
					{items.map((item) => (
						<SidebarMenuItem key={item.title}>
							{item.items ? (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<SidebarMenuButton size="sm">
											<item.icon />
											<span>{item.title}</span>
											<ChevronRight className="ml-auto" />
										</SidebarMenuButton>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										side="right"
										align="start"
										className="w-48"
									>
										<DropdownMenuGroup>
											{item.items.map((child) => (
												<DropdownMenuItem key={child.title} asChild>
													<Link to={child.url} className="cursor-pointer">
														{child.title}
													</Link>
												</DropdownMenuItem>
											))}
										</DropdownMenuGroup>
									</DropdownMenuContent>
								</DropdownMenu>
							) : item.external ? (
								<SidebarMenuButton asChild size="sm">
									<a
										href={item.url}
										target="_blank"
										rel="noopener noreferrer"
									>
										<item.icon />
										<span>{item.title}</span>
									</a>
								</SidebarMenuButton>
							) : (
								<NavLink to={item.url}>
									{({ isActive }) => (
										<>
											<SidebarMenuButton asChild isActive={isActive} size="sm">
												<span>
													<item.icon />
													<span>{item.title}</span>
												</span>
											</SidebarMenuButton>
											{item.badge && (
												<SidebarMenuBadge>{item.badge}</SidebarMenuBadge>
											)}
										</>
									)}
								</NavLink>
							)}
						</SidebarMenuItem>
					))}
				</SidebarMenu>
			</SidebarGroupContent>
		</SidebarGroup>
	);
}
